import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

// Public, no-auth page describing how a user deletes their account and data.
// Doubles as the account-deletion URL for Google Play's Data Safety form.
const DeleteAccountPage = () => {
  return (
    <div className="min-h-screen bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-medium text-fg mb-4">
            Delete Your Account
          </h1>
          <p className="text-xl text-fg-2 max-w-2xl mx-auto">
            You can permanently delete your Vegetablah account and all of your data at any time.
          </p>
          <p className="text-sm text-fg-3 mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Delete from within the app */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Delete from within the app</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              The fastest way is to delete your account yourself — it takes effect immediately.
              This works the same in the Vegetablah Android app and on the web at{' '}
              <a href="https://vegetablah.com" className="text-fg-2 hover:underline">vegetablah.com</a>.
            </p>
            <ol className="list-decimal list-inside text-fg-2 space-y-2">
              <li>Sign in to your account.</li>
              <li>Open the profile menu, then <span className="font-medium text-fg">Settings</span>.</li>
              <li>Scroll to <span className="font-medium text-fg">Delete account</span> and tap <span className="font-medium text-fg">Delete my account</span>.</li>
              <li>Type <span className="font-mono font-medium text-fg">DELETE</span> to confirm, and re-enter your password if your account uses one.</li>
              <li>Confirm. Your account and data are erased right away.</li>
            </ol>
          </CardContent>
        </Card>

        {/* What gets deleted */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">What gets deleted</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              Deletion is <span className="font-medium text-fg">permanent and cannot be undone</span>.
              We erase everything associated with your account, including:
            </p>
            <ul className="list-disc list-inside text-fg-2 space-y-2">
              <li>Your profile, login credentials, and profile picture</li>
              <li>All health and wellness entries — nutrition, supplements, workouts, hydration, and biometrics</li>
              <li>Your targets, trajectories, predictions, and coaching insights</li>
              <li>Connected-device links (e.g. Fitbit) and any stored access tokens, which we also revoke at the provider</li>
              <li>Reminders, notification settings, and logging templates</li>
            </ul>
            <p className="text-fg-2 leading-relaxed mt-4">
              Nothing personal to you is retained. Data is removed from our live systems immediately; routine
              encrypted backups roll off on their normal cycle (within 30 days).
            </p>
          </CardContent>
        </Card>

        {/* Can't access your account */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Can’t access your account?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed">
              If you can’t sign in, email us at{' '}
              <a href="mailto:support@vegetablah.com" className="text-fg-2 hover:underline">
                support@vegetablah.com
              </a>{' '}
              from the address on your account and ask us to delete it. We’ll verify it’s you and remove your
              account and all associated data.
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-fg-3">
          See our{' '}
          <Link to="/privacy-policy" className="text-fg-2 hover:underline">Privacy Policy</Link>{' '}
          for how we handle your data.
        </p>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
